import { useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, User, ExternalLink, FileText, Receipt } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  useJob,
  useProposal,
  useProposalItems,
  useInvoices,
} from "@/hooks/use-api";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { PropertyHero } from "@/components/v2/property-hero";
import { JobStageTracker } from "@/components/v2/job-stage-tracker";
import { LifecycleTimeline } from "@/components/v2/timeline/lifecycle-timeline";
import { useEntityLifecycle } from "@/hooks/use-entity-lifecycle";
import { EmptyState } from "@/components/v2/empty-state";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { JobPhotoGallery } from "@/components/v2/media/job-photo-gallery";
import { PhotoPreviewStrip } from "@/components/v2/media/photo-preview-strip";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function KpiTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string | null;
}) {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-3">
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-[color:var(--color-text-hi)]">
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 typo-label text-[color:var(--color-text-md)] truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

function LinkedChip({
  icon,
  primary,
  secondary,
  onClick,
  muted,
}: {
  icon: ReactNode;
  primary: string;
  secondary?: string | null;
  onClick?: () => void;
  muted?: boolean;
}) {
  const Wrapper: keyof JSX.IntrinsicElements = onClick ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left ${
        onClick ? "hover:bg-[color:var(--color-elevated)] cursor-pointer" : ""
      }`}
    >
      <span
        className={`shrink-0 ${
          muted
            ? "text-[color:var(--color-text-lo)]"
            : "text-[color:var(--color-text-md)]"
        }`}
      >
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span
          className={`block truncate typo-body font-medium ${
            muted
              ? "text-[color:var(--color-text-lo)] italic"
              : "text-[color:var(--color-text-hi)]"
          }`}
        >
          {primary}
        </span>
        {secondary && (
          <span className="block truncate typo-label text-[color:var(--color-text-md)]">
            {secondary}
          </span>
        )}
      </span>
    </Wrapper>
  );
}

function formatDaysOnSite(
  startedAt?: string | null,
  completedAt?: string | null,
  estimatedHours?: number | null,
): { value: string; sub?: string | null } {
  if (!startedAt) {
    return { value: "—", sub: null };
  }
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
  const planned = estimatedHours
    ? Math.max(1, Math.ceil(estimatedHours / 8))
    : null;
  return {
    value: planned ? `${days} / ${planned}` : `${days}`,
    sub: planned
      ? days <= planned
        ? `${planned - days}d ahead`
        : `${days - planned}d behind`
      : null,
  };
}

function formatScheduledWindow(
  start?: string | null,
  end?: string | null,
  date?: string | null,
): string | null {
  if (start && end) return `${formatDate(start)} → ${formatDate(end)}`;
  if (date) return formatDate(date);
  return null;
}

export default function V2JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Controlled so the Overview preview strip can deep-link into the Photos tab.
  const [tab, setTab] = useState("overview");

  const { data, isLoading, isError } = useJob(id!);

  // ADV-1347: DB-derived lifecycle milestones (the "moment" axis), read from
  // core.entity_lifecycle_v via GET /core/entity-lifecycle. Replaces the
  // client deriver for jobs; the JobStageTracker above stays the
  // "current-state" axis (decision T2 — two axes, never duplication).
  const { data: lifecycle } = useEntityLifecycle("job", id);
  const lifecycleItems = lifecycle?.items ?? [];

  const job = data?.job as Row | undefined;
  const location = (job?.location as Row | null) ?? null;
  const proposalId = (job?.proposal_id as string | null) ?? "";
  const dealId = (job?.deal_id as string | null) ?? "";

  const { data: proposalData } = useProposal(proposalId);
  const { data: itemsData } = useProposalItems(proposalId);
  // Gate on dealId so we don't fetch the entire invoices list when no deal is linked
  // (buildQuery drops empty-string params, which would return unrelated account invoices).
  const { data: invoicesData } = useInvoices(
    { deal_id: dealId, limit: 5 },
    { enabled: !!dealId },
  );

  const proposal = proposalData?.proposal as Row | undefined;
  const items = (itemsData?.items ?? []) as Row[];
  const invoices = (invoicesData?.invoices ?? []) as Row[];

  const displayId = useMemo(() => {
    return (job?.display_id as string | undefined) || (id?.slice(0, 8) ?? "");
  }, [job, id]);

  const titleSuffix = useMemo(() => {
    return (
      (job?.name as string | undefined) ||
      (job?.job_type as string | undefined) ||
      "Job"
    );
  }, [job]);

  const address = useMemo(() => {
    if (!location) return null;
    const formatted = location.formatted_address as string | null;
    if (formatted) return formatted;
    const parts = [
      location.street_line_1,
      location.city,
      location.state,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  }, [location]);

  const subhead = useMemo(() => {
    const customer = job?.customer_name as string | undefined;
    if (address && customer) return `${address} · ${customer}`;
    return address ?? customer ?? null;
  }, [address, job]);

  const stage = (job?.status as string | null) ?? "new";

  const scheduledWindow = useMemo(
    () =>
      formatScheduledWindow(
        job?.scheduled_start_time as string | null,
        job?.scheduled_end_time as string | null,
        job?.scheduled_date as string | null,
      ),
    [job],
  );

  const daysOnSite = useMemo(
    () =>
      formatDaysOnSite(
        job?.started_at as string | null,
        job?.completed_at as string | null,
        job?.estimated_hours as number | null,
      ),
    [job],
  );

  const totalRevenue = (job?.total_revenue as number | null) ?? null;
  const totalCost = (job?.total_cost as number | null) ?? null;
  const margin = (job?.margin as number | null) ?? null;
  const marginPercent =
    totalRevenue && totalRevenue > 0 && margin !== null
      ? Math.round((margin / totalRevenue) * 100)
      : null;

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        role="status"
        aria-label="Loading job"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (isError || (!isLoading && !job)) {
    const errTitle = isError ? "Failed to load job" : "Job not found";
    const errDescription = isError
      ? "Something went wrong. Please try again."
      : "This job may have been deleted.";
    return (
      <>
        <Breadcrumb className="mb-4 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/jobs"
                className="text-[color:var(--color-text-lo)]"
              >
                Jobs
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{id ?? "..."}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <EmptyState title={errTitle} description={errDescription} />
      </>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Breadcrumbs + per-page actions cluster */}
      <div className="mb-3 flex items-center justify-between">
        <Breadcrumb className="typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="/jobs"
                className="text-[color:var(--color-text-lo)]"
              >
                Jobs
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-[color:var(--color-text-lo)]">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>{displayId}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <DetailPageActions entityLabel="Job" />
        </div>
      </div>

      {/* 3-tab shell (Overview · Activity · Records) — same as Pattern A for consistency */}
      <Tabs value={tab} onValueChange={setTab} className="flex flex-col">
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Activity
          </TabsTrigger>
          <TabsTrigger
            value="photos"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Photos
          </TabsTrigger>
          <TabsTrigger
            value="records"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Records
          </TabsTrigger>
        </TabsList>

        {/* ── Overview ── */}
        <TabsContent value="overview" className="mt-0">
          <div className="flex gap-6 pt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 space-y-5">
              {/* Title block — Linear-style page heading first under tabs (Pattern B canonical) */}
              <div>
                <h1 className="typo-heading text-[color:var(--color-text-hi)]">
                  <span className="typo-mono">{displayId}</span>
                  {titleSuffix && (
                    <>
                      <span className="text-[color:var(--color-text-lo)] mx-2">
                        —
                      </span>
                      <span>{titleSuffix}</span>
                    </>
                  )}
                </h1>
                {subhead && (
                  <p className="mt-0.5 typo-body text-[color:var(--color-text-md)]">
                    {subhead}
                  </p>
                )}
              </div>

              {/* Inline properties strip */}
              <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
                <StatusBadge status={stage} />
                {scheduledWindow && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>{scheduledWindow}</span>
                  </>
                )}
                {totalRevenue != null && totalRevenue > 0 && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span className="typo-mono font-medium">
                      {formatCurrency(totalRevenue)}
                    </span>
                  </>
                )}
                {job?.created_at != null && (
                  <>
                    <span className="text-[color:var(--color-text-lo)]">·</span>
                    <span>Created {formatDate(job.created_at as string)}</span>
                  </>
                )}
              </div>

              {/* Stage tracker (horizontal pipeline — Pattern B inline) */}
              <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4">
                <JobStageTracker stage={stage} />
              </div>

              {/* KPI row (4 tiles) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile
                  label="Value"
                  value={
                    totalRevenue != null ? formatCurrency(totalRevenue) : "—"
                  }
                  sub={
                    job?.deal_number != null
                      ? `Deal ${job.deal_number as string}`
                      : null
                  }
                />
                <KpiTile
                  label="Days on site"
                  value={daysOnSite.value}
                  sub={daysOnSite.sub}
                />
                <KpiTile
                  label="Crew"
                  value={
                    (job?.assigned_crew_id as string | null)
                      ? "Assigned"
                      : "Unassigned"
                  }
                  sub={null}
                />
                <KpiTile
                  label="Materials cost"
                  value={totalCost != null ? formatCurrency(totalCost) : "—"}
                  sub={
                    marginPercent != null ? `${marginPercent}% margin` : null
                  }
                />
              </div>

              {/* Property hero (when location attached) */}
              {address && (
                <PropertyHero
                  address={address}
                  lat={(location?.latitude as number | null) ?? undefined}
                  lon={(location?.longitude as number | null) ?? undefined}
                />
              )}

              {/* Scope card (line items from linked proposal) */}
              {proposalId && (
                <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
                  <div className="flex items-center justify-between px-4 pt-3">
                    <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                      Scope
                    </span>
                    {proposal?.proposal_number != null && (
                      <button
                        type="button"
                        onClick={() => navigate(`/proposals/${proposalId}`)}
                        className="typo-label text-[color:var(--color-accent)] hover:underline"
                      >
                        From Proposal {proposal.proposal_number as string} →
                      </button>
                    )}
                  </div>
                  <div className="mt-2">
                    {items.length === 0 ? (
                      <p className="px-4 py-3 typo-body text-[color:var(--color-text-lo)]">
                        No scope items found on the linked proposal.
                      </p>
                    ) : (
                      items.map((item, i) => (
                        <div
                          key={(item.id as string) ?? i}
                          className="flex items-baseline justify-between gap-3 px-4 py-3 border-t border-[color:var(--color-border)]"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="typo-body font-medium text-[color:var(--color-text-hi)] truncate">
                              {(item.name as string | null) ||
                                (item.product_name as string | null) ||
                                "Line item"}
                            </div>
                            {(item.quantity != null ||
                              item.unit_price != null) && (
                              <div className="typo-label text-[color:var(--color-text-md)]">
                                {item.quantity != null
                                  ? String(item.quantity)
                                  : ""}
                                {item.unit_price != null
                                  ? ` @ ${formatCurrency(item.unit_price as number)}`
                                  : ""}
                              </div>
                            )}
                          </div>
                          <div className="typo-mono font-medium text-[color:var(--color-text-hi)]">
                            {item.line_total != null
                              ? formatCurrency(item.line_total as number)
                              : item.total != null
                                ? formatCurrency(item.total as number)
                                : "—"}
                          </div>
                        </div>
                      ))
                    )}
                    {totalRevenue != null && totalRevenue > 0 && (
                      <div className="flex items-baseline justify-between px-4 py-3 border-t border-[color:var(--color-border)]">
                        <span className="typo-label text-[color:var(--color-text-md)] uppercase tracking-wide">
                          Total
                        </span>
                        <span className="typo-mono font-semibold text-[color:var(--color-text-hi)]">
                          {formatCurrency(totalRevenue)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Linked records row (Customer · Proposal · Invoice chips) */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {job?.customer_name != null && (
                  <LinkedChip
                    icon={<User className="h-4 w-4" />}
                    primary={job.customer_name as string}
                    secondary="Customer"
                    onClick={
                      job.contact_role_id
                        ? () =>
                            navigate(
                              `/customers/${job.contact_role_id as string}`,
                            )
                        : undefined
                    }
                  />
                )}
                {proposalId && proposal?.proposal_number != null && (
                  <LinkedChip
                    icon={<FileText className="h-4 w-4" />}
                    primary={proposal.proposal_number as string}
                    secondary={
                      proposal?.status
                        ? `${formatDate(proposal.submit_date as string | null)}`.trim() ||
                          "Proposal"
                        : "Proposal"
                    }
                    onClick={() => navigate(`/proposals/${proposalId}`)}
                  />
                )}
                {invoices.length > 0 ? (
                  <LinkedChip
                    icon={<Receipt className="h-4 w-4" />}
                    primary={
                      (invoices[0]?.invoice_number as string | null) ||
                      "Invoice"
                    }
                    secondary={
                      invoices[0]?.status
                        ? String(invoices[0].status)
                        : "Linked invoice"
                    }
                    onClick={() =>
                      navigate(`/invoices/${invoices[0]?.id as string}`)
                    }
                  />
                ) : (
                  <LinkedChip
                    icon={<Receipt className="h-4 w-4" />}
                    primary="Pending"
                    secondary="Generates on completion"
                    muted
                  />
                )}
              </div>

              {/* Photos preview strip — recent-first, deep-links to the Photos
                  tab; renders nothing until the job has photos (§I3.1, §7) */}
              <PhotoPreviewStrip
                entityType="job"
                entityId={id!}
                onViewAll={() => setTab("photos")}
              />

              {/* Activity timeline — full feed at bottom of main column
                  per Pattern B (Work-line) */}
              <RailSection title="Activity">
                <EntityActivityFeed
                  entityType="job"
                  entityId={id!}
                  emptyDescription="Messages, calls, and notes on this job appear here once the job is active."
                />
              </RailSection>
            </div>

            {/* Right rail */}
            <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
              <RailSection title="Properties">
                <InfoRow label="Status" value={formatStageLabel(stage)} />
                <InfoRow
                  label="Type"
                  value={(job?.job_type as string | null) ?? null}
                />
                <InfoRow
                  label="Customer"
                  value={(job?.customer_name as string | null) ?? null}
                />
                <InfoRow label="Property" value={address} />
                <InfoRow label="Scheduled" value={scheduledWindow} />
                <InfoRow
                  label="Started"
                  value={
                    job?.started_at
                      ? formatDate(job.started_at as string)
                      : null
                  }
                />
                <InfoRow
                  label="Completed"
                  value={
                    job?.completed_at
                      ? formatDate(job.completed_at as string)
                      : null
                  }
                />
                <InfoRow
                  label="Created"
                  value={
                    job?.created_at
                      ? formatDate(job.created_at as string)
                      : null
                  }
                />
              </RailSection>

              {/* Progress (Value · Cost · Margin) */}
              {(totalRevenue != null ||
                totalCost != null ||
                margin != null) && (
                <RailSection title="Progress">
                  <InfoRow
                    label="Value"
                    value={
                      totalRevenue != null ? formatCurrency(totalRevenue) : null
                    }
                  />
                  <InfoRow
                    label="Cost"
                    value={totalCost != null ? formatCurrency(totalCost) : null}
                  />
                  <InfoRow
                    label="Margin"
                    value={
                      margin != null
                        ? `${formatCurrency(margin)}${marginPercent != null ? ` (${marginPercent}%)` : ""}`
                        : null
                    }
                  />
                </RailSection>
              )}

              {/* Activity preview — compact 4-row rail */}
              <RailSection title="Activity">
                <EntityActivityPreview entityType="job" entityId={id!} />
              </RailSection>
            </div>
          </div>
        </TabsContent>

        {/* ── Activity ── lifecycle timeline (moment axis) + activity feed */}
        <TabsContent value="activity" className="mt-0 pt-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            <section className="lg:w-[340px] lg:shrink-0">
              <h3 className="mb-3 typo-label text-[color:var(--color-text-md)]">
                Lifecycle
              </h3>
              <LifecycleTimeline
                items={lifecycleItems}
                emptyState="Lifecycle milestones appear here as this job progresses — scheduling, work start, and completion land on the timeline as they happen."
              />
            </section>
            <section className="min-w-0 flex-1 lg:border-l lg:border-[color:var(--color-border)] lg:pl-8">
              <h3 className="mb-3 typo-label text-[color:var(--color-text-md)]">
                Activity
              </h3>
              <EntityActivityFeed
                entityType="job"
                entityId={id!}
                emptyDescription="Messages, calls, and notes on this job appear here once the job is active."
              />
            </section>
          </div>
        </TabsContent>

        {/* ── Photos ── */}
        <TabsContent value="photos" className="mt-0 pt-6">
          <JobPhotoGallery entityType="job" entityId={id!} />
        </TabsContent>

        {/* ── Records ── */}
        <TabsContent value="records" className="mt-0 pt-6 space-y-6">
          {dealId ? (
            <div>
              <h2 className="typo-body font-semibold text-[color:var(--color-text-hi)] mb-3">
                Linked records
              </h2>
              <div className="flex flex-col gap-2">
                {job?.customer_name != null && job?.contact_role_id != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(`/customers/${job.contact_role_id as string}`)
                    }
                    className="justify-start gap-2"
                  >
                    <User className="h-4 w-4" />
                    Customer · {job.customer_name as string}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {proposalId && proposal?.proposal_number != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/proposals/${proposalId}`)}
                    className="justify-start gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Proposal · {proposal.proposal_number as string}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {dealId && job?.deal_number != null && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/opportunities/${dealId}`)}
                    className="justify-start gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Opportunity · {job.deal_number as string}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                )}
                {invoices.map((inv) => (
                  <Button
                    key={inv.id as string}
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/invoices/${inv.id as string}`)}
                    className="justify-start gap-2"
                  >
                    <Receipt className="h-4 w-4" />
                    Invoice · {(inv.invoice_number as string) ?? "—"} ·{" "}
                    {String(inv.status ?? "")}
                    <ExternalLink className="h-3.5 w-3.5 ml-auto" />
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              title="No linked records"
              description="Link this job to a customer, proposal, or invoice to see related records here."
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function formatStageLabel(stage: string): string {
  const map: Record<string, string> = {
    new: "New",
    scheduled: "Scheduled",
    in_progress: "In Progress",
    completed: "Completed",
    canceled: "Canceled",
  };
  return map[stage] ?? stage;
}
